// Image and text optimization utilities for the admin panel

/**
 * Compress and resize an image file
 * @param {File} file - The image file to optimize
 * @param {Object} options - Optimization options
 * @returns {Promise<string>} - Base64 encoded optimized image
 */
export const optimizeImage = (file, options = {}) => {
  const {
    maxWidth = 800,
    maxHeight = 800,
    quality = 0.8,
    format = 'image/jpeg'
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        
        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw the image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to optimized format
        const optimizedDataUrl = canvas.toDataURL(format, quality);
        
        resolve(optimizedDataUrl);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
      
      img.src = e.target.result;
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Validate and sanitize text input
 * @param {string} text - The text to sanitize
 * @param {Object} options - Sanitization options
 * @returns {string} - Sanitized text
 */
export const sanitizeText = (text, options = {}) => {
  const {
    maxLength = 500,
    allowHtml = false,
    trimWhitespace = true
  } = options;
  
  let sanitized = text || '';
  
  // Trim whitespace
  if (trimWhitespace) {
    sanitized = sanitized.trim();
  }
  
  // Remove HTML tags if not allowed
  if (!allowHtml) {
    sanitized = sanitized.replace(/<[^>]*>/g, '');
  }
  
  // Truncate to max length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  // Normalize whitespace
  sanitized = sanitized.replace(/\s+/g, ' ');
  
  return sanitized;
};

/**
 * Format price string to standard format
 * @param {string} price - The price string to format
 * @returns {string} - Formatted price string
 */
export const formatPrice = (price) => {
  if (!price) return '';
  
  // Remove extra spaces
  let formatted = price.trim().replace(/\s+/g, ' ');
  
  // Ensure proper format: "X,XXX RSD / XXXg"
  // Try to extract number and unit
  const priceMatch = formatted.match(/(\d[\d,.\s]*)\s*(RSD|EUR|USD)?\s*\/?\s*(\d+\s*[gG]|kg|KG)?/i);
  
  if (priceMatch) {
    const amount = priceMatch[1].replace(/\s/g, '');
    const currency = priceMatch[2] || 'RSD';
    const unit = priceMatch[3] || '500g';
    
    return `${amount} ${currency.toUpperCase()} / ${unit}`;
  }
  
  return formatted;
};

/**
 * Generate SEO-friendly alt text from product name
 * @param {Object} name - Product name object with translations
 * @param {string} language - Current language
 * @returns {string} - SEO-friendly alt text
 */
export const generateAltText = (name, language = 'en') => {
  const productName = name[language] || name.en || 'Honey Product';
  return `${productName} - Prirodan Med - Organic Honey from Serbia`;
};

/**
 * Validate image URL or base64
 * @param {string} imageUrl - The image URL to validate
 * @returns {boolean} - Whether the image is valid
 */
export const isValidImage = (imageUrl) => {
  if (!imageUrl) return false;
  
  // Check if base64
  if (imageUrl.startsWith('data:image/')) {
    return true;
  }
  
  // Check if valid URL
  try {
    const url = new URL(imageUrl);
    return ['http:', 'https:'].includes(url.protocol);
  } catch {
    return false;
  }
};

/**
 * Get optimized image dimensions for different contexts
 * @param {string} context - Where the image will be used
 * @returns {Object} - Dimensions object
 */
export const getImageDimensions = (context) => {
  const dimensions = {
    thumbnail: { maxWidth: 150, maxHeight: 150, quality: 0.7 },
    card: { maxWidth: 400, maxHeight: 400, quality: 0.8 },
    detail: { maxWidth: 800, maxHeight: 800, quality: 0.85 },
    hero: { maxWidth: 1200, maxHeight: 800, quality: 0.9 }
  };
  
  return dimensions[context] || dimensions.card;
};

/**
 * Calculate estimated file size from base64
 * @param {string} base64 - Base64 encoded string
 * @returns {number} - Estimated size in KB
 */
export const getBase64Size = (base64) => {
  if (!base64) return 0;
  
  // Remove data URL prefix if present
  const base64Data = base64.split(',')[1] || base64;
  
  // Calculate size: base64 is ~33% larger than original
  const sizeInBytes = (base64Data.length * 3) / 4;
  return Math.round(sizeInBytes / 1024);
};

export default {
  optimizeImage,
  sanitizeText,
  formatPrice,
  generateAltText,
  isValidImage,
  getImageDimensions,
  getBase64Size
};
